<template>
    <div
        class="new-character-page text-light min-vh-100 d-flex flex-column pb-10"
    >
        <!-- Page Header -->
        <header
            class="p-3 border-bottom border-secondary d-flex justify-content-between align-items-center"
        >
            <h2 class="m-0">New Character</h2>
        </header>

        <!-- Tabs Navigation -->
        <ul class="nav nav-tabs mt-3 px-3">
            <li class="nav-item">
                <a
                    class="nav-link active"
                    data-bs-toggle="tab"
                    href="#archetype"
                    ref="archetypeTab"
                    >Archetype</a
                >
            </li>
            <li class="nav-item">
                <a
                    class="nav-link"
                    :class="{ disabled: !isArchetypeAndBackgroundSelected }"
                    data-bs-toggle="tab"
                    href="#skills"
                >
                    Skills & Talents
                </a>
            </li>
            <li class="nav-item">
                <a
                    class="nav-link"
                    :class="{ disabled: !isArchetypeAndBackgroundSelected }"
                    data-bs-toggle="tab"
                    href="#abilities"
                    @click="updateSelectVisibility"
                >
                    Abilities
                </a>
            </li>
            <li class="nav-item">
                <a
                    class="nav-link"
                    :class="{ disabled: !showFinalTab }"
                    data-bs-toggle="tab"
                    href="#details"
                >
                    Final Details
                </a>
            </li>
        </ul>

        <!-- Tab Content -->
        <div class="tab-content">
            <!-- Archetype -->
            <div id="archetype" class="container-fluid tab-pane active">
                <br />
                <div class="row">
                    <div class="col-sm-6">
                        <div class="input-group mb-3">
                            <label
                                class="input-group-text"
                                for="archetypeSelect"
                                >Archetype</label
                            >
                            <select
                                class="form-select"
                                id="archetypeSelect"
                                @change="updateCharacterArchetype"
                            >
                                <option value="" disabled selected>
                                    Please select...
                                </option>
                                <option
                                    v-for="archetype in archetypes"
                                    :key="archetype.archetype"
                                    :value="archetype.archetype"
                                >
                                    {{ archetype.archetype }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-sm-6">
                        <div class="input-group mb-3">
                            <label
                                class="input-group-text"
                                for="backgroundSelect"
                                >Background</label
                            >
                            <select
                                class="form-select"
                                id="backgroundSelect"
                                @change="updateCharacterBackground"
                                :disabled="!newCharacterArchetype?.archetype"
                            >
                                <option value="" disabled selected>
                                    Please select...
                                </option>
                                <optgroup
                                    v-for="(
                                        backgrounds, archetype
                                    ) in groupedBackgrounds"
                                    :key="archetype"
                                    :label="archetype"
                                >
                                    <option
                                        v-for="background in backgrounds"
                                        :key="background.background"
                                        :value="background.background"
                                    >
                                        {{ background.background }}
                                    </option>
                                </optgroup>
                            </select>
                        </div>
                    </div>

                    <!-- Archetype details -->
                    <div
                        class="col-sm-6"
                        v-if="
                            newCharacterArchetype &&
                            newCharacterArchetype.archetype
                        "
                    >
                        <div class="row">
                            <div class="col-sm-12">
                                <h5>Selected Archetype Details</h5>
                            </div>
                            <div class="col-sm-12 mb-2">
                                <strong>Archetype:</strong>
                                {{ newCharacterArchetype.archetype }}
                            </div>
                            <div class="col-sm-12 mb-2">
                                <strong>Description:</strong>
                                {{ newCharacterArchetype.description }}
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-6" v-else>
                        <div class="no-data-message mt-5 text-center">
                            Please select an Archetype.
                        </div>
                    </div>

                    <!-- Background details -->
                    <div class="col-sm-6">
                        <div
                            class="row"
                            v-if="newCharacterArchetype?.background"
                        >
                            <div class="col-sm-12 mb-2">
                                <h5>Selected Background Details</h5>
                            </div>
                            <div class="col-sm-6 mb-2">
                                <strong>Background:</strong>
                                {{
                                    newCharacterArchetype.background.background
                                }}
                            </div>
                            <div class="col-sm-6 mb-2">
                                <strong>Archetype:</strong>
                                {{ newCharacterArchetype.background.archetype }}
                            </div>
                            <div class="col-sm-12 mb-2">
                                <strong>Description:</strong>
                                {{
                                    newCharacterArchetype.background.description
                                }}
                            </div>
                            <div class="col-sm-6 mb-2">
                                <strong>Ability:</strong>
                                {{ newCharacterArchetype.background.ability }}
                                <p>
                                    {{
                                        newCharacterArchetype.background
                                            .abilityDescription
                                    }}
                                </p>
                            </div>
                            <div class="col-sm-6 mb-2">
                                <strong>Flaw:</strong>
                                {{ newCharacterArchetype.background.flaw }}
                                <p>
                                    {{
                                        newCharacterArchetype.background
                                            .flawDescription
                                    }}
                                </p>
                            </div>
                        </div>
                        <div v-else>
                            <div class="no-data-message mt-5 text-center">
                                Please select a Background.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Skills -->
            <div id="skills" class="container-fluid tab-pane fade">
                <br />
                <div class="row" v-if="newCharacterArchetype">
                    <div class="col-sm-12"><h2>Skills & Talents</h2></div>

                    <StatsDisplay
                        v-if="
                            newCharacterArchetype && newCharacterArchetype.stats
                        "
                        v-model:stats="newCharacterArchetype.stats"
                        :readonly="true"
                        :diceRoller="false"
                        :theme="theme"
                    />

                    <div class="col-sm-12"><hr /></div>

                    <div
                        class="col-sm-6 text center"
                        v-if="newCharacterArchetype.core != null"
                    >
                        <h3>Strength</h3>
                        <h4>
                            {{
                                capitalize(
                                    skillList[
                                        resolveCoreKey(
                                            newCharacterArchetype.core
                                        )
                                    ].name
                                )
                            }}
                        </h4>
                    </div>

                    <div class="col-sm-6" v-else>
                        <div class="row">
                            <div class="col-sm-12"><h3>Strengths</h3></div>
                            <div class="col-sm-6">
                                <h4>Talent</h4>
                                <select
                                    class="form-select"
                                    @change="
                                        (e) =>
                                            handleAttributeChange(
                                                e,
                                                'talent',
                                                'talent',
                                                newCharacterArchetype.strengths
                                                    .talent
                                            )
                                    "
                                >
                                    <option disabled selected>
                                        Please select...
                                    </option>
                                    <option
                                        v-for="skill in skillList"
                                        :key="skill.value"
                                        :value="skill.name"
                                        :disabled="isDisabled(skill.name)"
                                    >
                                        {{ capitalize(skill.name) }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-sm-6">
                                <h4>Skill</h4>
                                <select
                                    class="form-select"
                                    @change="
                                        (e) =>
                                            handleAttributeChange(
                                                e,
                                                'skill',
                                                'skill',
                                                newCharacterArchetype.strengths
                                                    .skill
                                            )
                                    "
                                >
                                    <option disabled selected>
                                        Please select...
                                    </option>
                                    <option
                                        v-for="skill in skillList"
                                        :key="skill.value"
                                        :value="skill.name"
                                        :disabled="isDisabled(skill.name)"
                                    >
                                        {{ capitalize(skill.name) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="col-sm-6">
                        <div class="row">
                            <div class="col-sm-12"><h3>Deficiency</h3></div>
                            <div
                                class="col-sm-6"
                                v-for="(
                                    value, key
                                ) in newCharacterArchetype.deficiency"
                                :key="key"
                            >
                                <h4>
                                    Skill
                                    {{ newCharacterArchetype.deficiency[key] }}
                                </h4>
                                <select
                                    class="form-select"
                                    @change="
                                        (e) =>
                                            handleAttributeChange(
                                                e,
                                                'skill',
                                                key,
                                                value
                                            )
                                    "
                                >
                                    <option disabled selected>
                                        Please select...
                                    </option>
                                    <option
                                        v-for="skill in skillList"
                                        :key="skill.value"
                                        :value="skill.name"
                                        :disabled="isDisabled(skill.name)"
                                    >
                                        {{ capitalize(skill.name) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Abilities -->
            <div id="abilities" class="container-fluid tab-pane fade">
                <SkillTree
                    v-if="newCharacterArchetype"
                    :initial="newCharacterArchetype"
                    @updateSelectedAbilityList="updateSelectedAbilityList"
                    @unlockAbility="unlockAbility"
                    @forgetAbility="forgetAbility"
                />
            </div>

            <!-- Details -->
            <div id="details" class="container-fluid tab-pane fade">
                <br />
                <div class="row" v-if="newCharacterArchetype">
                    <div class="col-sm-12"><h2>Final Details</h2></div>
                    <div class="col-sm-6">
                        <div class="input-group mb-3">
                            <label class="input-group-text" for="nameInput"
                                >Name</label
                            >
                            <input
                                type="text"
                                class="form-control"
                                id="nameInput"
                                v-model="newCharacterArchetype.name"
                            />
                        </div>

                        <div class="row">
                            <div class="col-sm-6">
                                <div class="input-group mb-3">
                                    <label
                                        class="input-group-text"
                                        for="levelInput"
                                        >Level</label
                                    >
                                    <input
                                        type="number"
                                        class="form-control"
                                        id="levelInput"
                                        v-model="newCharacterArchetype.level"
                                        min="1"
                                        max="15"
                                    />
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <div class="input-group mb-3">
                                    <label
                                        class="input-group-text"
                                        for="ageInput"
                                        >Age</label
                                    >
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="ageInput"
                                        v-model="newCharacterArchetype.age"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="input-group mb-3">
                            <label class="input-group-text" for="genderInput"
                                >Gender</label
                            >
                            <input
                                type="text"
                                class="form-control"
                                id="genderInput"
                                v-model="newCharacterArchetype.gender"
                            />
                        </div>

                        <div class="input-group mb-3">
                            <label class="input-group-text" for="fearInput"
                                >Fear</label
                            >
                            <input
                                type="text"
                                class="form-control"
                                id="fearInput"
                                v-model="newCharacterArchetype.fear"
                            />
                        </div>

                        <div class="mb3">
                            <label for="backstoryInput" class="form-label"
                                >Backstory</label
                            >
                            <textarea
                                class="form-control"
                                id="backstoryInput"
                                rows="6"
                                v-model="newCharacterArchetype.backstory"
                            ></textarea>
                        </div>

                        <div class="mb-3">
                            <label for="formFile" class="form-label"
                                >Headshot</label
                            >
                            <input
                                class="form-control"
                                type="file"
                                id="avatarInput"
                                @change="addAvatar"
                            />
                        </div>
                    </div>

                    <div class="col-sm-6">
                        <div class="text-center">
                            <img
                                v-if="newCharacterArchetype.avatar"
                                :src="newCharacterArchetype.avatar"
                                alt="Avatar"
                                class="img-fluid"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <footer
            class="footer fixed-bottom text-light p-3 d-flex justify-content-end gap-2"
        >
            <button class="btn btn-secondary" @click="goBack(true)">
                <font-awesome-icon icon="arrow-left" class="me-1" />Back
            </button>
            <button
                class="btn btn-primary"
                @click="addCharacter"
                :disabled="!readyToSave"
            >
                <font-awesome-icon icon="save" class="me-1" />Save
            </button>
        </footer>
    </div>
</template>

<script>
import { Tab } from "bootstrap";
import { useRosterStore } from "@/stores/index";
import SkillTree from "./SkillTree.vue";
import Stats from "./Stats.vue";
import { resizeImage } from "../utils/resizeImage";

export default {
    components: {
        SkillTree,
        StatsDisplay: Stats,
    },
    setup() {
        const store = useRosterStore(); // make store available to the instance
        return { store };
    },
    data() {
        return {
            rosterStore: useRosterStore(),
            newCharacterArchetype: {},
            archetypes: [],
            backgrounds: [],
            backgroundAbilities: [],
            flaws: [],
            abilities: [],
            skillList: {
                grit: {
                    value: 0,
                    name: "grit",
                    setBy: null,
                    adjustmentType: null,
                    adjustment: 0,
                },
                wit: {
                    value: 1,
                    name: "wit",
                    setBy: null,
                    adjustmentType: null,
                    adjustment: 0,
                },
                smarts: {
                    value: 2,
                    name: "smarts",
                    setBy: null,
                    adjustmentType: null,
                    adjustment: 0,
                },
                heart: {
                    value: 3,
                    name: "heart",
                    setBy: null,
                    adjustmentType: null,
                    adjustment: 0,
                },
                luck: {
                    value: 4,
                    name: "luck",
                    setBy: null,
                    adjustmentType: null,
                    adjustment: 0,
                },
                strange: {
                    value: 5,
                    name: "strange",
                    setBy: null,
                    adjustmentType: null,
                    adjustment: 0,
                },
            },
            showFinalTab: false,
            selectedAbilityList: null,
        };
    },
    created() {
        // pull all game data from store instead of JSON files
        const store = this.rosterStore;
        this.archetypes = store.archetypes;
        this.backgrounds = store.backgrounds;
        this.backgroundAbilities = store.backgroundAbilities;
        this.flaws = store.flaws;
        this.abilities = store.abilities;
    },
    computed: {
        theme() {
            return Number(this.store.theme) || 0;
        },
        groupedBackgrounds() {
            const groups = {};
            this.backgrounds.forEach((bg) => {
                if (!groups[bg.archetype]) groups[bg.archetype] = [];
                groups[bg.archetype].push(bg);
            });
            return groups;
        },
        isArchetypeAndBackgroundSelected() {
            return (
                this.newCharacterArchetype &&
                this.newCharacterArchetype.background
            );
        },
        readyToSave() {
            return (
                this.showFinalTab === true &&
                this.newCharacterArchetype.name &&
                this.newCharacterArchetype.age &&
                this.newCharacterArchetype.gender &&
                this.newCharacterArchetype.fear
            );
        },
    },
    methods: {
        // all your existing methods unchanged except:
        updateCharacterArchetype(event) {
            const selectedName = event.target.value;
            const selected = this.archetypes.find(
                (a) => a.archetype === selectedName
            );
            if (!selected) return;

            // preserve some user-filled fields (if any)
            const preserved = this.newCharacterArchetype
                ? {
                      background: this.newCharacterArchetype.background || null,
                      name: this.newCharacterArchetype.name || "",
                      age: this.newCharacterArchetype.age || "",
                      gender: this.newCharacterArchetype.gender || "",
                      fear: this.newCharacterArchetype.fear || "",
                      backstory: this.newCharacterArchetype.backstory || "",
                      level: 1,
                  }
                : {
                      background: null,
                      name: "",
                      age: "",
                      gender: "",
                      fear: "",
                      backstory: "",
                      level: 1,
                  };

            // deep copy the selected archetype and merge preserved values
            const copy = JSON.parse(JSON.stringify(selected));
            Object.assign(copy, preserved);

            // always reset abilities array
            copy.abilities = [];

            // replace reference so watchers update consistently
            this.newCharacterArchetype = copy;
            this.selectedAbilityList = copy.archetype;

            // reset skill-list adjustments
            this.resetAttributeChange();

            // ✅ mark the core in skillList immediately
            const coreKey = this.resolveCoreKey(copy.core);
            if (coreKey && this.skillList[coreKey]) {
                this.skillList[coreKey].setBy = "core";
                this.skillList[coreKey].adjustmentType = "core";
                this.skillList[coreKey].adjustment = 0;
            }

            // Reset all selects to "Please select..."
            const selects = document.querySelectorAll("#skills select");
            selects.forEach((select) => {
                select.selectedIndex = 0;
            });

            this.showFinalTab = false;
        },
        updateCharacterBackground(event) {
            const selectedBackgroundName = event.target.value;
            const selectedBackground = this.backgrounds.find(
                (background) => background.background === selectedBackgroundName
            );
            if (selectedBackground) {
                const backgroundAbility = this.backgroundAbilities.find(
                    (ability) => ability.name === selectedBackground.ability
                );
                const flaw = this.flaws.find(
                    (f) => f.name === selectedBackground.flaw
                );

                this.newCharacterArchetype.background = JSON.parse(
                    JSON.stringify(selectedBackground)
                );
                this.newCharacterArchetype.background.abilityDescription =
                    backgroundAbility?.description || null;
                this.newCharacterArchetype.background.flawDescription =
                    flaw?.description || null;
            }
        },
        goBack(check) {
            if (check === true) {
                if (
                    confirm(
                        "Are you sure you want to go back? Unsaved changes will be lost."
                    )
                ) {
                    this.resetModalData();
                    this.$router.push("/");
                }
            } else {
                this.resetModalData();
                this.$router.push("/");
            }
        },
        async addAvatar(event) {
            const file = event.target.files[0];
            if (file && file.type.startsWith("image/")) {
                this.newCharacterArchetype.avatar = await resizeImage(file);
            }
        },
        resolveCoreKey(coreVal) {
            if (coreVal == null) return null;
            if (typeof coreVal === "number") {
                return Object.keys(this.skillList).find(
                    (k) => this.skillList[k].value === coreVal
                );
            }
            // assume string key
            return coreVal;
        },
        allSelectsFilled() {
            const selects = document.querySelectorAll("#skills select");
            const displayedSelects = Array.from(selects).filter(
                (select) => select.offsetParent !== null
            );
            if (displayedSelects.length === 0) {
                return;
            }
            const allFilled = displayedSelects.every(
                (select) => select.value && select.value !== "Please select..."
            );
            if (allFilled) {
                this.showFinalTab = true;
            }
        },
        capitalize(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        resetModalData() {
            this.resetAttributeChange();
            this.updateSelectVisibility();
            const archetypeTab = this.$refs.archetypeTab;
            const tab = new Tab(archetypeTab);
            tab.show();
            const archetypeSelect = document.getElementById("archetypeSelect");
            const backgroundSelect =
                document.getElementById("backgroundSelect");
            const avatarInput = document.getElementById("avatarInput");
            if (avatarInput) avatarInput.value = "";
            if (archetypeSelect) archetypeSelect.selectedIndex = 0;
            if (backgroundSelect) backgroundSelect.selectedIndex = 0;
            this.newCharacterArchetype = null;
            this.showFinalTab = false;
            this.showSkillTab = false;
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
            }
        },
        updateSelectedAbilityList(abilityList) {
            this.selectedAbilityList = abilityList;
        },
        updateSelectVisibility() {
            // Update the selectVisibility array to trigger reactivity
            this.selectVisibility = Array.from(
                document.querySelectorAll("#skills select")
            ).map((select) => select.offsetParent !== null);
        },
        clearAttributeChange(setBy, type) {
            Object.keys(this.skillList).forEach((key) => {
                const skill = this.skillList[key];
                if (skill.setBy === setBy) {
                    this.newCharacterArchetype.stats[key][type] -=
                        skill.adjustment;
                    skill.setBy = null;
                    skill.adjustmentType = null;
                    skill.adjustment = 0;
                }
            });
        },
        resetAttributeChange() {
            Object.keys(this.skillList).forEach((key) => {
                this.skillList[key].setBy = null;
                this.skillList[key].adjustmentType = null;
                this.skillList[key].adjustment = 0;
            });
        },
        handleAttributeChange(event, type, setBy, value) {
            this.clearAttributeChange(setBy, type);
            this.newCharacterArchetype.stats[event.target.value][type] += value;
            this.skillList[event.target.value].setBy = setBy;
            this.skillList[event.target.value].adjustmentType = type;
            this.skillList[event.target.value].adjustment = value;
            this.allSelectsFilled();
        },
        isDisabled(skillValue) {
            const entry = this.skillList[skillValue];
            return entry ? entry.setBy !== null : false;
        },
        unlockAbility(ability) {
            if (!this.newCharacterArchetype.abilities) {
                this.newCharacterArchetype.abilities = [];
            }

            const exists = this.newCharacterArchetype.abilities.some(
                (a) => a.id === ability.id
            );
            if (!exists) {
                this.newCharacterArchetype.abilities.push(ability);
            }
        },
        forgetAbility(ability) {
            if (!this.newCharacterArchetype.abilities) {
                this.newCharacterArchetype.abilities = [];
                return; // nothing to remove
            }

            this.newCharacterArchetype.abilities =
                this.newCharacterArchetype.abilities.filter(
                    (a) => a.id !== ability.id
                );
        },
        addCharacter() {
            // Ensure the store is defined
            const maxId = this.rosterStore.characters.reduce(
                (max, character) => Math.max(max, character.id),
                0
            );
            this.newCharacterArchetype.id = maxId + 1;
            this.newCharacterArchetype.luckbank = 0;
            this.newCharacterArchetype.notes = "";
            this.newCharacterArchetype.armour = [];
            this.newCharacterArchetype.weapons = [];
            this.newCharacterArchetype.flaws = [];
            let backgroundFlaw = {
                name: this.newCharacterArchetype.background.flaw,
                description:
                    this.newCharacterArchetype.background.flawDescription,
                archetype: this.newCharacterArchetype.core,
            };
            this.newCharacterArchetype.flaws.push(backgroundFlaw);
            this.newCharacterArchetype.fatalFlaws = [];
            this.rosterStore.addCharacter(this.newCharacterArchetype);

            this.goBack(false);
        },
        selectArchetype(archetype) {
            this.newCharacterArchetype = JSON.parse(JSON.stringify(archetype));
        },
        selectBackground(background) {
            if (!this.newCharacterArchetype) return;
            this.newCharacterArchetype.background = JSON.parse(
                JSON.stringify(background)
            );
        },
    },
    watch: {
        newCharacterArchetype(newVal) {
            this.resetAttributeChange();
            if (newVal && newVal.core != null) {
                const coreKey = this.resolveCoreKey(newVal.core); // <-- map number→string
                if (coreKey && this.skillList[coreKey]) {
                    this.skillList[coreKey].setBy = "core";
                    this.skillList[coreKey].adjustmentType = "core";
                    this.skillList[coreKey].adjustment = 0;
                }
            }
        },
    },
};
</script>

<style scoped>
.new-character-page {
    display: flex;
    flex-direction: column;
}
.disabled {
    display: none !important;
}
.nav-tabs .nav-link.disabled {
    pointer-events: none;
    opacity: 0.5;
}
.nav-tabs .nav-link {
    cursor: pointer;
    color: white !important;
}
footer {
    background-color: rgba(0, 0, 0, 0.6); /* Dark gray background */
}

.nav-tabs {
    background-color: transparent; /* Dark gray background */
}

.nav-tabs .nav-link {
    color: white; /* White text */
}

.nav-tabs .nav-link.active {
    background-color: #495057 !important; /* Slightly lighter gray for active tab */
    color: white !important; /* Ensure active tab text is white */
}
.nav-tabs .navlink.disabled {
    color: transparent !important;
}
</style>

